import type { Appointment } from '@/api/customer/customerStateQuery/types';
import type { ApiPlatform } from '@/utils/getPlatform';

/**
 * Platform values this app writes on checkout (see `getApiPlatform`). Matched
 * exactly: staff-created bookings arrive as `Commander Android`,
 * `Commander iPhone`, `Commander iPad`, etc. and must not count.
 */
const CUSTOMER_APP_PLATFORMS: ReadonlySet<string> = new Set<ApiPlatform>([
  'iOS',
  'Android',
]);

export interface AppStoreReviewEligibilityInput {
  /** All upcoming + past appointments from the customer state. */
  appointments: Appointment[];
  /** Previously persisted successful count, or undefined if never stored. */
  lastSuccessfulAppointmentCount: number | undefined;
  /** LD `successfulBookingsInterstitial` (default 2). */
  successfulBookingsInterstitial: number;
  /** LD `successfulBookingsInAppReview` (default 3). */
  successfulBookingsInAppReview: number;
}

export interface AppStoreReviewEligibilityResult {
  /** Whether the app should prompt for review now. */
  shouldPrompt: boolean;
  /** Successful appointment count computed for this evaluation. */
  successfulAppointments: number;
}

/**
 * Counts appointments that qualify toward review eligibility: booked from the
 * customer app (platform exactly `iOS` or `Android`) and paid (online or in
 * shop).
 *
 * Native iOS uses a `contains("iOS")` substring check. That is deliberately
 * not mirrored here because the Android analogue would also match
 * `Commander Android` (SQR-15430).
 */
function countSuccessfulMobileAppointments(
  appointments: Appointment[],
): number {
  return appointments.filter((appointment) => {
    const isMobile = CUSTOMER_APP_PLATFORMS.has(appointment.platform ?? '');
    const isPaid =
      appointment.paymentStatus === 'paid' || appointment.paidInShop === true;
    return isMobile && isPaid;
  }).length;
}

/**
 * Decides whether to surface the review prompt, mirroring native iOS
 * `shouldPresentAppStoreReviewIfNecessary`.
 *
 * - Requires at least `successfulBookingsInterstitial` successful mobile
 *   bookings.
 * - First-ever eligible check (no stored count) prompts.
 * - Subsequent checks prompt once the count has advanced by a positive multiple
 *   of `successfulBookingsInAppReview` since the last prompt.
 *
 * The caller is responsible for persisting `lastSuccessfulAppointmentCount`
 * (only when `shouldPrompt` is true), matching iOS which writes the count
 * inside the prompt branches.
 */
export function evaluateAppStoreReviewEligibility({
  appointments,
  lastSuccessfulAppointmentCount,
  successfulBookingsInterstitial,
  successfulBookingsInAppReview,
}: AppStoreReviewEligibilityInput): AppStoreReviewEligibilityResult {
  const successfulAppointments =
    countSuccessfulMobileAppointments(appointments);

  if (
    appointments.length === 0 ||
    successfulAppointments < successfulBookingsInterstitial
  ) {
    return { shouldPrompt: false, successfulAppointments };
  }

  const previousCount = lastSuccessfulAppointmentCount;

  // First-ever eligible check: no count stored yet.
  if (previousCount === undefined) {
    return { shouldPrompt: true, successfulAppointments };
  }

  // Repeat check: prompt every `successfulBookingsInAppReview` additional
  // successful bookings since the last prompt.
  const advanced = successfulAppointments - previousCount;
  const shouldPrompt =
    advanced > 0 && advanced % successfulBookingsInAppReview === 0;

  return { shouldPrompt, successfulAppointments };
}
